import { useCallback, useMemo } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import type { Booking, Lesson, Student, StudentProfile } from '@court-sync/shared';
import type { PaginationState } from '@/components/ui';
import { bookingService } from '../services/booking';
import { lessonService } from '../services/lesson';
import { academyStudentService } from '../services/academyStudentService';
import { STALE_TIME, queryClient } from '../queryClient';
import { queryKeys, type BookingListFilters } from './keys';
import { unwrapApiData, unwrapApiDataOrEmpty } from './unwrap';

import { EMPTY_BOOKING_FILTERS, serializeFilters } from './stableFilters';
import type { AcademyStudentsPageResult } from './students';

function findLessonInCache(queryClient: QueryClient, lessonId: string): Lesson | undefined {
  const detail = queryClient.getQueryData<Lesson>(queryKeys.lessons.detail(lessonId));
  if (detail) return detail;

  const listEntries = queryClient.getQueriesData<Lesson[]>({ queryKey: queryKeys.lessons.lists() });
  for (const [, lessons] of listEntries) {
    const match = lessons?.find((lesson) => lesson.id === lessonId);
    if (match) return match;
  }
  return undefined;
}

function findStudentInCache(
  queryClient: QueryClient,
  studentId: string
): StudentProfile | Student | undefined {
  const listEntries = queryClient.getQueriesData<AcademyStudentsPageResult>({
    queryKey: queryKeys.students.lists(),
  });
  for (const [, page] of listEntries) {
    const match = page?.students?.find((student) => student.id === studentId);
    if (match) return match;
  }
  return undefined;
}

export function useBookings(filters?: BookingListFilters) {
  const queryClient = useQueryClient();
  const resolvedFilters = filters ?? EMPTY_BOOKING_FILTERS;
  const serializedFilters = serializeFilters(resolvedFilters);
  const stableFilters = useMemo(
    () => resolvedFilters,
    [serializedFilters, resolvedFilters]
  );
  const queryKey = queryKeys.bookings.list(stableFilters);

  const { data: bookings = [], isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await bookingService.getBookings(stableFilters);
      return unwrapApiDataOrEmpty(response);
    },
    enabled: !!(stableFilters.academyId || stableFilters.customerId || stableFilters.lessonId),
    staleTime: STALE_TIME.SHORT,
  });

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.bookings.lists() });
  }, [queryClient]);

  const createMutation = useMutation({
    mutationFn: async (data: {
      lessonId: string;
      termId: string;
      academyId: string;
      studentId: string;
    }) => {
      const response = await bookingService.createBooking(data);
      const id = unwrapApiData(response, 'Failed to create booking').id;
      const detail = await bookingService.getBooking(id);
      return unwrapApiData(detail, 'Failed to fetch created booking');
    },
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<Omit<Booking, 'id' | 'createdAt' | 'updatedAt'>>;
    }) => {
      await bookingService.updateBooking(id, data);
      const detail = await bookingService.getBooking(id);
      return unwrapApiData(detail, 'Failed to fetch updated booking');
    },
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Booking[]>(queryKey);
      queryClient.setQueryData<Booking[]>(queryKey, (old) =>
        old?.map((b) => (b.id === id ? { ...b, ...data, updatedAt: new Date() } : b))
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
    },
    onSuccess: invalidate,
  });

  const cancelMutation = useMutation({
    mutationFn: async (id: string) => {
      await bookingService.cancelBooking(id);
      const detail = await bookingService.getBooking(id);
      return unwrapApiData(detail, 'Failed to fetch cancelled booking');
    },
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await bookingService.deleteBooking(id);
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Booking[]>(queryKey);
      queryClient.setQueryData<Booking[]>(queryKey, (old) => old?.filter((b) => b.id !== id));
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
    },
    onSuccess: invalidate,
  });

  const getBookingsByAcademy = useCallback(
    (academyId: string) => bookings.filter((b) => b.academyId === academyId),
    [bookings]
  );
  const getBookingsByLesson = useCallback(
    (lessonId: string) => bookings.filter((b) => b.lessonId === lessonId),
    [bookings]
  );
  const getBookingsByCustomer = useCallback(
    (customerId: string) => bookings.filter((b) => b.customerId === customerId),
    [bookings]
  );
  const getBookingById = useCallback((id: string) => bookings.find((b) => b.id === id), [bookings]);

  const refresh = useCallback(() => refetch(), [refetch]);

  return {
    bookings: filters?.academyId
      ? getBookingsByAcademy(filters.academyId)
      : filters?.lessonId
        ? getBookingsByLesson(filters.lessonId)
        : filters?.customerId
          ? getBookingsByCustomer(filters.customerId)
          : bookings,
    isLoading,
    error: error instanceof Error ? error.message : error ? String(error) : null,
    refresh,
    createBooking: createMutation.mutateAsync,
    updateBooking: (id: string, data: Parameters<typeof updateMutation.mutateAsync>[0]['data']) =>
      updateMutation.mutateAsync({ id, data }),
    cancelBooking: cancelMutation.mutateAsync,
    deleteBooking: deleteMutation.mutateAsync,
    getBookingsByAcademy,
    getBookingsByLesson,
    getBookingsByCustomer,
    getBookingById,
  };
}

export async function fetchBookingsQuery(
  filters?: BookingListFilters,
  options?: { force?: boolean }
): Promise<void> {
  const key = queryKeys.bookings.list(filters ?? {});
  if (options?.force) {
    await queryClient.invalidateQueries({ queryKey: key });
  }
  await queryClient.fetchQuery({
    queryKey: key,
    queryFn: async () => {
      const response = await bookingService.getBookings(filters);
      return unwrapApiDataOrEmpty(response);
    },
    staleTime: STALE_TIME.SHORT,
  });
}

export interface AcademyBookingsPageFilters {
  status?: string;
  lessonId?: string;
  termId?: string;
}

export interface AcademyBookingsPageResult {
  bookings: Booking[];
  pagination: PaginationState;
  lessonById: Record<string, Lesson>;
  students: Record<string, StudentProfile | Student>;
}

function isPaginatedBookingsQueryKey(queryKey: readonly unknown[]): boolean {
  return queryKey.includes('paginated');
}

export function patchBookingInQueries(
  bookingId: string,
  patch: Partial<Omit<Booking, 'id' | 'createdAt' | 'updatedAt'>>,
  client: QueryClient = queryClient
): void {
  const listEntries = client.getQueriesData<Booking[]>({
    queryKey: queryKeys.bookings.lists(),
    predicate: (query) => !isPaginatedBookingsQueryKey(query.queryKey),
  });
  for (const [key, bookings] of listEntries) {
    if (!bookings?.some((booking) => booking.id === bookingId)) continue;
    client.setQueryData<Booking[]>(
      key,
      bookings.map((booking) =>
        booking.id === bookingId ? { ...booking, ...patch, updatedAt: new Date() } : booking
      )
    );
  }

  const pageEntries = client.getQueriesData<AcademyBookingsPageResult>({
    queryKey: queryKeys.bookings.lists(),
    predicate: (query) => isPaginatedBookingsQueryKey(query.queryKey),
  });
  for (const [key, page] of pageEntries) {
    if (!page?.bookings?.some((booking) => booking.id === bookingId)) continue;
    client.setQueryData<AcademyBookingsPageResult>(key, {
      ...page,
      bookings: page.bookings.map((booking) =>
        booking.id === bookingId ? { ...booking, ...patch, updatedAt: new Date() } : booking
      ),
    });
  }
}

export function invalidateBookingsQueries(): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.bookings.lists() });
}

const ACADEMY_BOOKINGS_PAGE_SIZE = 20;

export function useAcademyBookingsPage(
  academyId?: string,
  page = 1,
  filters: AcademyBookingsPageFilters = {},
  options?: { enabled?: boolean }
) {
  const queryClient = useQueryClient();
  const enabled = options?.enabled ?? true;
  const serializedFilters = serializeFilters(filters);
  const stableFilters = useMemo(() => filters, [serializedFilters, filters]);
  const queryKey = queryKeys.bookings.paginated(academyId ?? '', page, stableFilters);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async (): Promise<AcademyBookingsPageResult> => {
      const result = await bookingService.getAcademyBookingsPaginated(
        academyId!,
        page,
        ACADEMY_BOOKINGS_PAGE_SIZE,
        stableFilters
      );
      const pageBookings = result.data;
      const lessonById: Record<string, Lesson> = {};
      const students: Record<string, StudentProfile | Student> = {};

      if (pageBookings.length > 0) {
        const lessonIds = [...new Set(pageBookings.map((b) => b.lessonId))];
        for (const id of lessonIds) {
          const cached = findLessonInCache(queryClient, id);
          if (cached) lessonById[id] = cached;
        }
        const missingLessonIds = lessonIds.filter((id) => !lessonById[id]);
        if (missingLessonIds.length > 0) {
          const lessonResponses = await Promise.all(
            missingLessonIds.map((id) => lessonService.getLesson(id))
          );
          lessonResponses.forEach((r) => {
            if (r.data) lessonById[r.data.id] = r.data;
          });
        }

        const studentIds = [...new Set(pageBookings.map((b) => b.studentId).filter(Boolean))];
        for (const id of studentIds) {
          const cached = findStudentInCache(queryClient, id);
          if (cached) students[id] = cached;
        }
        const missingStudentIds = studentIds.filter((id) => !students[id]);
        if (missingStudentIds.length > 0) {
          const studentResponses = await Promise.all(
            missingStudentIds.map((id) => academyStudentService.getStudentProfile(academyId!, id))
          );
          studentResponses.forEach((r) => {
            if (r.success && r.data) students[r.data.id] = r.data;
          });
        }
      }

      return {
        bookings: pageBookings,
        pagination: result.pagination,
        lessonById,
        students,
      };
    },
    enabled: !!academyId && enabled,
    staleTime: STALE_TIME.MEDIUM,
    placeholderData: keepPreviousData,
  });

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.bookings.lists() });
  }, [queryClient]);

  const refresh = useCallback(
    async (opts?: { force?: boolean }) => {
      if (opts?.force) {
        await queryClient.invalidateQueries({ queryKey });
      }
      await refetch();
    },
    [queryClient, queryKey, refetch]
  );

  return {
    bookings: data?.bookings ?? [],
    pagination: data?.pagination ?? null,
    lessonById: data?.lessonById ?? {},
    students: data?.students ?? {},
    isLoading,
    error: error instanceof Error ? error.message : error ? String(error) : null,
    refresh,
    invalidate,
  };
}
